/**
 * Phase 2 Wave 2 (Task 1) — typed event bus for per-entity mutation
 * notifications.
 *
 * # Why a bus and not a callback prop?
 *
 * The renderer's incremental-update path (BatchedLayer.patchEntity, the
 * tessellation cache invalidator, the RBush dirty marker) all need to
 * react to the SAME mutation event. A single subscribe/emit API
 * fan-outs to N consumers without coupling `commitMutation` to the
 * renderer's bookkeeping (which would import the entire scene module
 * graph from inside the state layer — circular and untestable).
 *
 * # Listener contract
 *
 *   - `kind: "added" | "updated" | "removed"` — what happened.
 *   - `id` — the entity id.
 *   - `spaceId` — the model / layout id the entity lives in.
 *   - `changedFields` (updates only) — coarse "what changed" bucket
 *     (`geometry` / `style` / `both` / `other`). Used by the renderer
 *     to decide whether tessellation cache can be reused (`style`,
 *     `other`) or must be busted (`geometry`, `both`).
 *
 * # Threading
 *
 * Single-threaded JS — every emit fans out synchronously in
 * subscription order. Listeners that need async work should schedule
 * it themselves; the bus does not await.
 *
 * # Cleanup
 *
 * `subscribeEntityChanged` returns an unsubscribe function. Tests +
 * unit `beforeEach` should call `clearEntityChangedListeners()` to wipe
 * the listener set between cases.
 */

import type { EntityChangedFields } from "./entityChangedClassify";

export type EntityChangedKind = "added" | "updated" | "removed";

export interface EntityChangedEvent {
  id: string;
  spaceId: string;
  kind: EntityChangedKind;
  /**
   * For `"updated"` events, identifies which class of fields changed.
   * Style-only changes can skip tessellation invalidation; geometry
   * changes must bust it. Absent for `"added"` / `"removed"` events.
   */
  changedFields?: EntityChangedFields;
}

type Listener = (event: EntityChangedEvent) => void;

const listeners = new Set<Listener>();

/**
 * Register a listener and return an unsubscribe function. The returned
 * function is idempotent (calling it twice is a no-op).
 */
export function subscribeEntityChanged(listener: Listener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

/**
 * Synchronously fan an event out to every registered listener in
 * subscription order. Throws are caught per-listener so one bad
 * subscriber can't poison the rest of the fan-out.
 */
export function emitEntityChanged(event: EntityChangedEvent): void {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (err) {
      // Don't let a single bad listener break the whole pipeline.
      if (typeof console !== "undefined") {
        console.error("[entityChangedBus] listener threw:", err);
      }
    }
  }
}

/**
 * Wipe the entire listener set. Test-only helper — production code
 * should never need this (cleanup happens via the unsubscribe handle).
 */
export function clearEntityChangedListeners(): void {
  listeners.clear();
}

/**
 * Snapshot the current listener count for diagnostics + tests.
 */
export function getEntityChangedListenerCount(): number {
  return listeners.size;
}
